import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PhoneOff, MessageSquareText, CalendarCheck, TrendingUp, Check, Shield, Bot, Hash, Voicemail, FlaskConical } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const DEMO_FEED = [
  { icon: PhoneOff, tone: "rose", title: "Missed call: Sample Caller", meta: "+1 (555) 010-0100 · unanswered" },
  { icon: MessageSquareText, tone: "slate", title: "IVR consent prompt played", meta: "\"…press 1 to consent to text messages, or press 2 to decline.\"" },
  { icon: Hash, tone: "emerald", title: "Caller pressed 1: consent recorded", meta: "Brand, timestamp, script version saved" },
  { icon: Bot, tone: "orange", title: "\"Your Roofing Co.: Thanks for calling! Sorry we missed you… Reply HELP for help, STOP to opt out.\"", meta: "First text, sent only after opt-in" },
  { icon: CalendarCheck, tone: "emerald", title: "Estimate booked: Tue 2:00 PM", meta: "Booked by the roofing company" },
];

const TONE = {
  rose: "bg-rose-500/10 border-rose-500/30 text-rose-400",
  slate: "bg-slate-800/60 border-slate-700 text-sky-400",
  emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  orange: "bg-orange-500/10 border-orange-500/30 text-orange-400",
};

const STEPS = [
  { icon: PhoneOff, label: "01", title: "Call goes unanswered", desc: "A homeowner calls the roofing company while the team is busy." },
  { icon: MessageSquareText, label: "02", title: "IVR asks permission", desc: "Before any text is sent, an automated voice prompt asks if the caller wants text messages, with rates, STOP/HELP, and policy disclosures." },
  { icon: Hash, label: "03", title: "Caller presses 1", desc: "Only an explicit press of 1 opts the caller in. The consent event is recorded. No press 1 means no text, and the caller can leave a voicemail." },
  { icon: Bot, label: "04", title: "First text, then follow-up", desc: "The first text identifies the company and includes STOP/HELP. AI then helps the roofing company follow up and book an estimate." },
];

const FEATURES = [
  { icon: Shield, title: "Consent-First IVR", desc: "Callers opt in by pressing 1 before any SMS. Every consent event is logged with brand, timestamp, and script version." },
  { icon: MessageSquareText, title: "AI-Assisted Replies", desc: "After opt-in, Claude drafts short, on-brand replies for the roofing company. Your team can take over anytime." },
  { icon: Voicemail, title: "Voicemail Fallback", desc: "Callers who decline texts can leave a voicemail. It's attached to the lead for a callback." },
  { icon: TrendingUp, title: "Lead Pipeline", desc: "Track each lead from first contact to booked estimate on one board." },
  { icon: CalendarCheck, title: "Estimate Scheduling", desc: "Book estimates on your team's calendar with double-booking prevention and Google Calendar sync." },
  { icon: Bot, title: "STOP / HELP Built In", desc: "Opt-out and help keywords are honored automatically, and STOP revokes consent immediately." },
];

const PLAN = {
  name: "RoofLead", price: 775, setup: 350, tag: "One plan for roofing businesses",
  features: ["Consent-first missed-call IVR", "AI-assisted SMS after opt-in", "Lead pipeline", "Estimate scheduling + calendar sync",
    "Team members & bookable estimators", "Analytics dashboard", "Guided onboarding & A2P registration support"],
};

function DemoFeed() {
  return (
    <Card className="bg-slate-900/90 border-slate-800 p-5 rounded-2xl shadow-2xl" data-testid="hero-demo-feed">
      <div className="flex items-center justify-between mb-4">
        <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/15 font-mono text-[10px] tracking-widest" data-testid="hero-demo-label">
          DEMO EXAMPLE · FICTIONAL DATA
        </Badge>
        <span className="text-xs text-slate-500 font-mono">illustration</span>
      </div>
      <div className="space-y-3">
        {DEMO_FEED.map((s, i) => (
          <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${TONE[s.tone]}`}>
            <s.icon className="w-4 h-4 mt-1 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-slate-100">{s.title}</p>
              <p className="text-xs text-slate-400 mt-1 font-mono">{s.meta}</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Hero({ navigate }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40" />
      <div className="absolute top-40 -left-40 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Badge className="bg-orange-500/10 text-orange-300 border border-orange-500/30 hover:bg-orange-500/10 mb-6 font-mono text-xs tracking-widest" data-testid="hero-status-badge">
              <FlaskConical className="w-3 h-3 mr-2" /> IN DEVELOPMENT &amp; TESTING · SOFTWARE FOR ROOFING COMPANIES
            </Badge>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]">
              Recover missed roofing calls, <span className="text-orange-500">with consent first.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed" data-testid="hero-description">
              RoofLead is a software platform for roofing companies. When a call goes unanswered, an automated voice prompt
              asks the caller for permission to text. Only callers who press 1 to opt in receive a text. AI then helps the
              roofing company follow up and book estimates.
            </p>
            <p className="mt-3 text-sm text-slate-400 max-w-xl" data-testid="hero-not-contractor">
              RoofLead is not a roofing contractor and does not provide roofing services.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => navigate("/signup")} data-testid="hero-cta-signup" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold text-base h-12 px-6 ember-glow">
                Create Account <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/sms-consent")} data-testid="hero-cta-consent" className="border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-100 h-12 px-6">
                See the Consent Flow
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Press-1 SMS consent</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> STOP &amp; HELP built in</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> No text without opt-in</div>
            </div>
          </motion.div>
        </div>
        <motion.div className="lg:col-span-5" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
          <DemoFeed />
        </motion.div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="relative py-24 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">How it works</p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">From missed call to consented conversation.</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <Card className="bg-slate-900/70 border-slate-800 p-6 h-full hover:border-orange-500/50 transition-colors group" data-testid={`how-step-${i + 1}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-11 h-11 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                    <s.icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 tracking-widest">{s.label}</span>
                </div>
                <h3 className="font-display text-xl font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoiCalculator({ navigate }) {
  const [missed, setMissed] = useState([18]);
  const [avgJob, setAvgJob] = useState([14000]);
  const rate = 0.22;
  const potential = missed[0] * 4 * avgJob[0] * rate;
  return (
    <section className="py-24 border-t border-slate-800 bg-gradient-to-b from-transparent to-slate-900/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">Illustrative calculator</p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-4">What could missed calls be costing you?</h2>
          <p className="text-slate-300 mb-8">Enter your own numbers. This is a hypothetical illustration, not a RoofLead customer result.</p>
          <div className="space-y-8">
            <div>
              <div className="flex justify-between mb-3">
                <label className="text-sm text-slate-300">Missed calls per week</label>
                <span className="font-mono text-lg text-orange-400 font-bold" data-testid="roi-missed-value">{missed[0]}</span>
              </div>
              <Slider value={missed} onValueChange={setMissed} min={1} max={60} step={1} data-testid="roi-missed-slider" />
            </div>
            <div>
              <div className="flex justify-between mb-3">
                <label className="text-sm text-slate-300">Your average job value</label>
                <span className="font-mono text-lg text-orange-400 font-bold" data-testid="roi-avgjob-value">${avgJob[0].toLocaleString()}</span>
              </div>
              <Slider value={avgJob} onValueChange={setAvgJob} min={2000} max={60000} step={500} data-testid="roi-avgjob-slider" />
            </div>
          </div>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-8 rounded-2xl">
          <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/15 font-mono text-[10px] tracking-widest mb-4" data-testid="roi-hypothetical-label">
            HYPOTHETICAL ESTIMATE
          </Badge>
          <p className="text-sm text-slate-400 mb-2 font-mono uppercase tracking-widest">Illustrative monthly value</p>
          <div className="font-display font-extrabold text-6xl text-emerald-400 font-mono tracking-tight" data-testid="roi-total">
            ${Math.round(potential).toLocaleString()}
          </div>
          <p className="text-slate-400 mt-3 text-sm" data-testid="roi-disclaimer">
            Assumes 22% of missed callers opt in by pressing 1 and later book a job. This is an assumption for illustration
            only, not a guarantee or a measured RoofLead result.
          </p>
          <div className="mt-8 pt-6 border-t border-slate-800 grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold font-mono text-white">{missed[0] * 4}</div>
              <div className="text-xs text-slate-500 mt-1">Missed/mo</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono text-orange-400">{Math.round(missed[0] * 4 * rate)}</div>
              <div className="text-xs text-slate-500 mt-1">Assumed booked</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono text-emerald-400">22%</div>
              <div className="text-xs text-slate-500 mt-1">Assumed rate</div>
            </div>
          </div>
          <Button onClick={() => navigate("/signup")} data-testid="roi-cta" className="w-full mt-6 bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold h-11">
            Create Account <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Card>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="py-24 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="mb-14">
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">Built for roofing companies</p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">Software that turns missed calls into consented follow-ups.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Card className="bg-slate-900/60 border-slate-800 p-6 h-full hover:border-orange-500/40 hover:bg-slate-900 transition-colors">
                <f.icon className="w-6 h-6 text-orange-400 mb-4" />
                <h3 className="font-display text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Compliance() {
  return (
    <section id="compliance" className="py-24 border-t border-slate-800 bg-slate-900/30" data-testid="compliance-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">Consent &amp; compliance</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">No text without a press of 1.</h2>
        <p className="text-slate-300 leading-relaxed mb-4">
          Texts are never sent to ask someone to opt in. Callers opt in on the phone by pressing 1 after hearing the
          consent message, or by checking an unchecked box on a web form. Callers who don't opt in get no texts and can
          leave a voicemail instead. Reply STOP at any time to opt out, or HELP for help.
        </p>
        <p className="text-slate-400 text-sm mb-6" data-testid="compliance-sender-note">
          For the current RoofLead software development &amp; testing program, text messages are sent by{" "}
          <span className="text-slate-200 font-semibold">Brandon Rinker Jr.</span>, the registered SMS sender. RoofLead is
          the software platform. No roofing services are offered under this program.
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          <Link to="/sms-consent" className="text-orange-400 hover:text-orange-300 underline" data-testid="compliance-sms-consent-link">SMS consent process</Link>
          <Link to="/privacy" className="text-orange-400 hover:text-orange-300 underline" data-testid="compliance-privacy-link">Privacy Policy</Link>
          <Link to="/terms" className="text-orange-400 hover:text-orange-300 underline" data-testid="compliance-terms-link">Terms of Service</Link>
        </div>
      </div>
    </section>
  );
}

function Pricing({ navigate }) {
  return (
    <section id="pricing" className="py-24 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3" data-testid="pricing-eyebrow">Simple, Transparent Pricing</p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Simple pricing for roofing businesses.</h2>
        </div>
        <div className="max-w-md mx-auto">
          <Card className="p-8 border bg-slate-900 border-orange-500/50 ember-glow relative" data-testid="pricing-plan-card">
            <p className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-2">{PLAN.tag}</p>
            <h3 className="font-display text-2xl font-bold mb-4">{PLAN.name}</h3>
            <div className="mb-2" data-testid="pricing-monthly">
              <span className="font-display text-5xl font-extrabold">${PLAN.price}</span>
              <span className="text-slate-500 ml-1">/month</span>
            </div>
            <p className="text-sm text-slate-300 mb-6" data-testid="pricing-setup-fee">
              + <span className="font-semibold text-orange-400">${PLAN.setup}</span> one-time setup fee
            </p>
            <ul className="space-y-3 mb-8">
              {PLAN.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button onClick={() => navigate("/signup")} data-testid="pricing-cta-rooflead" className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold">
              Create Account
            </Button>
          </Card>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 selection:bg-orange-500 selection:text-slate-900">
      <nav className="sticky top-0 z-50 border-b border-slate-800/80 bg-[#0B0F17]/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <Logo />
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-300 font-medium">
            <a href="#features" className="hover:text-orange-400 transition-colors" data-testid="nav-features">Features</a>
            <a href="#how" className="hover:text-orange-400 transition-colors" data-testid="nav-how">How it works</a>
            <a href="#compliance" className="hover:text-orange-400 transition-colors" data-testid="nav-compliance">Consent</a>
            <a href="#pricing" className="hover:text-orange-400 transition-colors" data-testid="nav-pricing">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="text-slate-200 hover:text-white hover:bg-slate-800">Login</Button>
            <Button onClick={() => navigate("/signup")} data-testid="nav-signup-btn" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold">
              Create Account <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      <Hero navigate={navigate} />
      <HowItWorks />
      <RoiCalculator navigate={navigate} />
      <Features />
      <Compliance />
      <Pricing navigate={navigate} />

      <section className="py-24 border-t border-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-emerald-500/10" />
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-12">
          <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight mb-6">Turn missed calls into consented conversations.</h2>
          <p className="text-lg text-slate-300 mb-8">RoofLead is in development &amp; testing. Create an account to explore the platform.</p>
          <Button size="lg" onClick={() => navigate("/signup")} data-testid="footer-cta-signup" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold text-base h-12 px-8 ember-glow">
            Create Account <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-6 text-sm">
            <Link to="/get-quote" className="text-slate-400 hover:text-orange-400 transition-colors" data-testid="footer-get-quote">Contact RoofLead</Link>
            <Link to="/sms-consent" className="text-slate-400 hover:text-orange-400 transition-colors" data-testid="footer-sms-consent">SMS Consent</Link>
            <Link to="/privacy" className="text-slate-400 hover:text-orange-400 transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="text-slate-400 hover:text-orange-400 transition-colors" data-testid="footer-terms">Terms</Link>
          </div>
          <p className="text-sm text-slate-500" data-testid="footer-tagline">© 2026 RoofLead. Software for roofing companies. Not a roofing contractor.</p>
        </div>
      </footer>
    </div>
  );
}
